import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import {
  DataGrid,
  gridFilteredRowsLookupSelector,
  gridFilteredSortedRowIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import { MdaClaim, MdaValueFilterField, MdaValueFilters } from "./mda-types";
import {
  getClaimRowId,
  getColumns,
  isClaimIncluded,
} from "./mda-table-helpers";
import {
  applyValueFilters,
  getDistinctValues,
  hasActiveValueFilters,
} from "./mda-value-filters";
import MdaColumnValueFilter from "./mda-column-value-filter";

const hasSameIds = (a: Set<string> | undefined, b: Set<string>) =>
  a !== undefined && a.size === b.size && [...b].every((id) => a.has(id));

interface MdaClaimsTableProps {
  claims: MdaClaim[];
  loading: boolean;
  fillHeight?: boolean;
  onClaimsChange: (claims: MdaClaim[]) => void;
  // orderedClaims is the grid as the user sees it (their sort and filter
  // applied), so "next claim" means the next row on screen.
  onViewRuleBreaks?: (claim: MdaClaim, orderedClaims: MdaClaim[]) => void;
}

const MdaClaimsTable = ({
  claims,
  loading,
  fillHeight = false,
  onClaimsChange,
  onViewRuleBreaks,
}: MdaClaimsTableProps) => {
  const apiRef = useGridApiRef();
  const [valueFilters, setValueFilters] = useState<MdaValueFilters>({});
  const [visibleRowIds, setVisibleRowIds] = useState<Set<string>>();

  const rows = useMemo(
    () => applyValueFilters(claims, valueFilters),
    [claims, valueFilters],
  );
  const visibleCount = visibleRowIds
    ? claims.filter((claim) => visibleRowIds.has(getClaimRowId(claim))).length
    : rows.length;

  // Runs after both the value filters and the grid's own filter are applied.
  const handleFilteredRows = () => {
    const gridRowIds = apiRef.current?.getAllRowIds().map(String) ?? [];
    // The grid can briefly hold the previous rows; only act once it matches.
    if (!hasSameIds(new Set(gridRowIds), new Set(rows.map(getClaimRowId)))) {
      return;
    }
    // Sorting is re-applied after this event, so the sorted selectors can
    // still miss newly added rows here; read the filter lookup instead.
    const lookup = gridFilteredRowsLookupSelector(apiRef);
    const ids = new Set(gridRowIds.filter((id) => lookup[id] !== false));
    setVisibleRowIds((current) => (hasSameIds(current, ids) ? current : ids));

    const hasHiddenTickedClaim = claims.some(
      (claim) => isClaimIncluded(claim) && !ids.has(getClaimRowId(claim)),
    );
    if (hasHiddenTickedClaim) {
      onClaimsChange(
        claims.map((claim) =>
          ids.has(getClaimRowId(claim)) ? claim : { ...claim, selected: true },
        ),
      );
    }
  };
  // Assigned during render because the grid fires the event from its own
  // effects, which run before this component's effects.
  const handleFilteredRowsRef = useRef(handleFilteredRows);
  handleFilteredRowsRef.current = handleFilteredRows;
  useEffect(
    () =>
      apiRef.current?.subscribeEvent("filteredRowsSet", () =>
        handleFilteredRowsRef.current(),
      ),
    [apiRef],
  );

  const renderValueFilter = (
    field: MdaValueFilterField,
    headerName: string,
  ) => (
    <MdaColumnValueFilter
      headerName={headerName}
      values={getDistinctValues(
        applyValueFilters(claims, valueFilters, field),
        field,
      )}
      excluded={valueFilters[field] ?? []}
      onApply={(excluded) =>
        setValueFilters((current) => ({ ...current, [field]: excluded }))
      }
    />
  );

  const handleViewRuleBreaks = onViewRuleBreaks
    ? (claim: MdaClaim) => {
        const claimsById = new Map(
          claims.map((item) => [getClaimRowId(item), item]),
        );
        const orderedClaims = gridFilteredSortedRowIdsSelector(apiRef)
          .map((id) => claimsById.get(String(id)))
          .filter((item): item is MdaClaim => Boolean(item));

        onViewRuleBreaks(claim, orderedClaims.length ? orderedClaims : claims);
      }
    : undefined;

  const columns = getColumns(claims, onClaimsChange, handleViewRuleBreaks, {
    visibleRowIds,
    renderValueFilter,
  });

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #dfe6f0",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        flex: fillHeight ? 1 : undefined,
        // Fill spare height on tall screens, but never shrink below a usable
        // number of rows; the page scrolls instead.
        minHeight: fillHeight ? 400 : undefined,
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid #dfe6f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography sx={{ color: "#1c2d49", fontWeight: 700 }}>
          {visibleCount !== claims.length
            ? `Results: ${visibleCount} of ${claims.length} claims`
            : `Results: ${claims.length} claims`}
        </Typography>
        {hasActiveValueFilters(valueFilters) && (
          <Button size="small" onClick={() => setValueFilters({})}>
            Clear column filters
          </Button>
        )}
      </Box>
      <Box
        sx={{
          flex: fillHeight ? 1 : undefined,
          height: fillHeight ? undefined : 430,
          minHeight: 0,
          width: "100%",
        }}
      >
        <DataGrid
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={getClaimRowId}
          loading={loading}
          pagination
          initialState={{ pagination: { paginationModel: { pageSize: 20 } } }}
          pageSizeOptions={[20, 40, 60, 100]}
          localeText={{ noRowsLabel: "No claims match the selected filters." }}
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f8fafc",
              color: "#24344d",
              fontWeight: 700,
            },
            "& .MuiDataGrid-cell": {
              color: "#46566e",
              fontSize: "0.875rem",
            },
            "& .MuiDataGrid-footerContainer": {
              minHeight: 52,
              overflow: "hidden",
            },
            "& .MuiTablePagination-root": {
              overflow: "hidden",
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default MdaClaimsTable;
